import React, { useEffect, useState, useMemo } from 'react';
import axiosClient from 'http/axios-client';
import { TextField, Grid, makeStyles, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { LoadingButtonNoGrid as LoadingButton } from 'components/basic/LoadingButton';
import { useQuery, useQueryClient } from 'react-query';
import prettier from 'prettier';
import parserHtml from 'prettier/parser-html';
import { LoaderSkeleton } from 'components/basic/LoaderSkeleton';
import { Parser } from 'html-to-react';
import { useAlerts } from '../hooks/alerts-hooks';
import { useItems } from '../hooks/atoms/items-hook';
import CollectionKeys from '../hooks/molecules/collection-keys';

const useStyles = makeStyles(() => {
  return {
    textArea: {
      height: '76vh',
      '& textarea': {
        height: '76vh !important',
        resize: 'vertical',
        overflow: 'scroll !important',
        overflowWrap: 'normal',
        wordWrap: 'unset',
        whiteSpace: 'pre',
        fontFamily: 'monospace',
        fontSize: '0.85em',
      },
    },
  };
});

const ignoredTemplated = [
  'Admin_Tool_User_Registration_Template',
  'Appointment_Confirmation_Customer',
  'Appointment_Confirmation_Scientist',
  'B2B_Registration_Template',
  'Customer_new_Doctor_Invitation',
  'Discount_Coupon_Referal_Friend',
  'Doctor_Registration_Template',
  'Emr_Customer_Sample_Registration_Template',
  'Emr_Doctor_Sample_Registration_Template',
  'Emr_New_Sample_Template',
  'Emr_Pending_Results_2_Template',
  'Emr_Pending_Results_Template',
  'Emr_Rusults_Template',
  'New_Registered_User_Template_Patient',
  'New_Registered_User_Template_new',
  'Not_Registered_Doctors_Patients_Kit_Registration_PGx',
  'Order_Confirmation_Template',
  'Package_Analysis_Approval_Template',
  'Package_Analysis_Not_Approved_Template',
  'Package_Approved_Template',
  'Package_Deploy_Not_Approved_Template',
  'Package_Management_Not_Approved_Template',
  'Package_PRS_SNPS_Approved_Template',
  'Package_PRS_SNPS_Not_Approved_Template',
  'Package_Quality_Not_Approved_Template',
  'Package_Report_Creation',
  'Package_Report_Enabled_Template',
  'Package_Report_Notification_Template',
  'Package_Report_Quality_Approved_Template',
  'Package_Report_Submitted_Template',
  'Package_Report_Template_Creation',
  'Package_Report_Texts_Notification',
  'Patient_Information_Doctor_Consents_PGx',
  'Patient_Information_Doctor_Not_Consents_PGx',
  'Purchase_CNS_PGx_DNA_Test',
  'Register_PGx_Kit_Not_Registered_Doctor',
  'Registered_Doctors_Inform_Patient_PGx_Report',
  'Registered_Doctors_Patients_Kit_Registration',
  'Registered_Doctors_Patients_Kit_Registration_PGx',
  'Registration_Kit_Completion_CNS_PGX_Registered',
  'Registration_Kit_Completion_F_a',
  'Registration_Kit_Completion_F_b',
  'Registration_Kit_Completion_G_a',
  'Registration_Kit_Completion_G_b',
  'Registration_Kit_Completion_G_c',
  'Registration_Kit_Completion_H',
  'Registration_Kit_Completion_I_a1',
  'Registration_Kit_Completion_I_a2',
  'Registration_Kit_Completion_I_b',
  'Registration_Kit_Completion_a_en_dr',
  'Registration_Kit_Completion_c',
  'Registration_Kit_Completion_c_Non_Registered',
  'Template_Email1',
  'Template_Email2',
  'Template_Email3',
  'Thematic_Package_Completion',
  'Thematic_Package_Creation_Molecular_Biologist_Notification',
  'Thematic_Package_Final_Template',
  'Thematic_Package_Molecular_Biologist_Final_Update',
  'Thematic_Package_Scientific_Update',
];

const CreateTemplateDialog = ({ open, onClose, onAccept, templateName, setTemplateName }) => {
  return (
    <Dialog
      onClose={ () => { onClose(); } }
      open={ open }
      fullWidth
      maxWidth='sm'
    >
      <DialogTitle>Create a template</DialogTitle>

      <DialogContent>
        <Grid container spacing={ 2 }>
          <Grid item xs={ 12 }>
            <TextField
              fullWidth
              value={ templateName }
              onChange={ (e) => { setTemplateName(e.target.value); } }
              label='Template name'
              variant='outlined'
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={ onClose } variant='contained'>
          Close
        </Button>
        <Button onClick={ onAccept } variant='contained' color='secondary'>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EmailTemplate = ({ selectedTemplate, setSelectedTemplate }) => {
  const [ templateHtml, setTemplateHtml ] = useState('');
  const [ templateSubject, setTemplateSubject ] = useState('');
  const { alertSuccess, alertError } = useAlerts();
  const [ description, setDescription ] = useState('');
  const [ EmailtemplateCategories, setEmailtemplateCategories ] = useState([]);

  const classes = useStyles();

  const [ error, setError ] = useState(false);

  useEffect(() => {
    setError(false);
  }, [ selectedTemplate ]);

  const { data: template, isLoading: templateLoading } = useQuery([ 'email-templates', selectedTemplate ], () => {
    return (
      axiosClient.get(`/mail/templates-content?template=${ selectedTemplate }`, {
        params: {
          _limit: -1,
        },
      })
    );
  });

  const { data: templateDescription, isLoading: templateDescriptionLoading } = useQuery([ 'email-templates-descriptions', selectedTemplate ], () => {
    return (
      axiosClient.get('email-templates', {
        params: {
          _limit: -1,
          name_eq: selectedTemplate,
        },
      })
    );
  });

  const {
    items: templateCategories, isLoading: templateCategoriesLoading,
  } = useItems(CollectionKeys.EMAIL_TEMPLATE_CATEGORIES, { filterQuery: { _limit: -1 } }, {
  });

  templateCategories.sort((a, b) => { return (a.name > b.name ? 1 : -1); });

  const queryClient = useQueryClient();

  useEffect(() => {
    if (!templateDescriptionLoading && 0 < templateDescription?.length) {
      setDescription(templateDescription?.[0]?.description ?? '');
    } else {
      setDescription('');
    }
  }, [ templateDescription, templateDescriptionLoading ]);

  useEffect(() => {
    if (!templateCategoriesLoading && templateCategories && templateDescription?.[0]) {
      setEmailtemplateCategories(templateDescription?.[0].email_template_categories);
    } else {
      setEmailtemplateCategories([]);
    }
  }, [ templateCategories, templateCategoriesLoading, templateDescription ]);

  useEffect(() => {
    if (!templateLoading) {
      let formattedHtml = template.HtmlPart;

      try {
        formattedHtml = prettier.format(template.HtmlPart, {
          parser: 'html',
          plugins: [ parserHtml ],
        });
      } catch (e) {
        console.error(e);
      }

      setTemplateHtml(formattedHtml);
      setTemplateSubject(template.SubjectPart);
    } else {
      setTemplateHtml('');
      setTemplateSubject('');
    }
  }, [ template, templateLoading ]);

  const [ updateLoading, setUpdateLoading ] = useState(false);
  const [ deleteLoading, setDeleteLoading ] = useState(false);

  const [ templateName, setTemplateName ] = useState('');

  const createTemplate = () => {
    axiosClient.post('/mail/templates-content', {
      TemplateName: templateName.replace(' ', '_'),
      SubjectPart: templateName,
      HtmlPart: templateName,
      TextPart: templateName,
    }).finally(() => {
      setDialogOpen(false);
      queryClient.invalidateQueries([ 'email-templates', 'all' ]);
      setSelectedTemplate(templateName.replace(' ', '_'));
    });
  };

  const updateTemplate = () => {
    setUpdateLoading(true);

    Promise.all([
      axiosClient.put('/mail/templates/editTemplate', {
        TemplateName: selectedTemplate,
        SubjectPart: templateSubject,
        TextPart: templateHtml,
        HtmlPart: templateHtml,
      }),
      updateTemplateInDatabase(),
    ])
      .then(() => {
        alertSuccess('Email updated successfully');
        queryClient.invalidateQueries([ 'email-templates', 'all' ]);
        queryClient.invalidateQueries('all-template-descriptions');
      }).catch((e) => {
        alertError('There was an error');
        console.error(e);
      })
      .finally(() => {
        setUpdateLoading(false);
      });
  };

  const deleteTemplate = () => {
    setDeleteLoading(true);
    axiosClient.delete(`/mail/templates/${ selectedTemplate }`)
      .then(() => {
        if (0 < templateDescription.length) {
          axiosClient.delete(`/email-templates/${ templateDescription[0].id }`);
        }
      })
      .then(() => {
        alertSuccess('Email updated successfully');
      }).catch((e) => {
        alertError('There was an error');
        console.error(e);
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  };

  const updateTemplateInDatabase = async () => {
    if (0 === templateDescription.length) {
      return axiosClient.post('/email-templates', {
        name: selectedTemplate,
        description,
        email_template_categories: EmailtemplateCategories.map((s) => { return s.id; }),
      });
    }
    return axiosClient.put(`/email-templates/${ templateDescription[0].id }`, {
      description,
      email_template_categories: EmailtemplateCategories.map((s) => { return s.id; }),
    });
  };

  const [ dialogOpen, setDialogOpen ] = useState(false);

  if (templateLoading || templateDescriptionLoading || templateCategoriesLoading) {
    return (
      <LoaderSkeleton />
    );
  }

  let reactComponent;

  try {
    const htmlToReactParser = Parser();
    reactComponent = htmlToReactParser.parse(templateHtml);
  } catch {
    setError(true);
  }

  return (
    <div>
      {dialogOpen
      && (
        <CreateTemplateDialog
          open={ dialogOpen }
          onClose={ () => { setDialogOpen(false); } }
          onAccept={ createTemplate }
          templateName={ templateName }
          setTemplateName={ setTemplateName }
        />
      )}
      <div style={ { display: 'flex', gap: '10px', justifyContent: 'space-between', marginBottom: '10px', alignItems: 'center' } }>
        <div>
          <TextField
            variant='outlined'
            value={ templateSubject }
            label='Subject'
            color='primary'
            size='small'
            onChange={ (e) => { setTemplateSubject(e.target.value); } }
            style={ { minWidth: '600px' } }
          />
        </div>

        <div>
          <LoadingButton
            variant='contained'
            color='secondary'
            onClick={ () => { setDialogOpen(true); } }
            buttonStyle={ { color: 'white' } }
            style={ { marginRight: '10px' } }
            label='Create'
          />
          <LoadingButton
            label='Update'
            variant='contained'
            style={ { marginRight: '10px' } }
            onClick={ updateTemplate }
            loading={ updateLoading }
            color='primary'
          />
          <LoadingButton
            variant='contained'
            buttonStyle={ { background: 'red', color: 'white' } }
            label='Delete'
            onClick={ deleteTemplate }
            loading={ deleteLoading }
          />
        </div>
      </div>

      <TextField
        fullWidth
        value={ description }
        multiline
        onChange={ (e) => { setDescription(e.target.value); } }
        variant='outlined'
        label='Description'
        style={ { marginBottom: '10px' } }
      />

      <Autocomplete
        options={ templateCategories }
        getOptionLabel={ (option) => { return option?.name; } }
        multiple
        size='small'
        color='primary'
        style={ { width: '600px', marginBottom: '10px' } }
        value={ EmailtemplateCategories }
        onChange={ (e, newValues) => { setEmailtemplateCategories(newValues); } }
        renderInput={ (params) => {
          return (
            <TextField
              { ...params }
              label='Template Category'
              variant='outlined'
            />
          );
        } }
      />

      <Grid container style={ { height: '76vh' } }>
        <Grid item xs={ 12 } lg={ 6 }>
          {
            error ? (
              <Typography variant='h6'>
                Html error
              </Typography>
            ) : (
              reactComponent
            )
          }
        </Grid>

        <Grid item xs={ 12 } lg={ 6 }>
          <TextField
            multiline
            className={ classes.textArea }
            value={ templateHtml }
            fullWidth
            variant='outlined'
            onChange={ (e) => {
              setTemplateHtml(e.target.value);
            } }
          />
        </Grid>
      </Grid>

    </div>
  );
};

const EmailTemplates = () => {
  const [ selectedTemplate, setSelectedTemplate ] = useState('');
  const [ selectedCategory, setSelectedCategory ] = useState([]);

  const { data: emailTemplateNames, isLoading: emailTemplateNamesLoading } = useQuery([ 'email-templates', 'all' ], async () => {
    const tempNames = [];

    let NextToken = '';

    while (NextToken !== undefined) {
      // eslint-disable-next-line no-await-in-loop
      const resp = await axiosClient.post('/mail/templates', { NextToken });
      NextToken = resp.NextToken;
      tempNames.push(...resp.TemplatesMetadata);
    }

    return tempNames
      .filter((tN) => {
        return 'true' !== process.env.REACT_APP_SHOW_ONLY_DEV_EMAIL || tN?.Name.startsWith('TESTENVIRONMENT_');
      });
  });

  const { data: templates } = useQuery('all-template-descriptions', async () => {
    const resp = await axiosClient.get('email-templates', {
      params: { _limit: -1 },
    });
    return resp;
  });

  const {
    items: templateCategories, isLoading: templateCategoriesLoading,
  } = useItems(CollectionKeys.EMAIL_TEMPLATE_CATEGORIES, { filterQuery: { _limit: -1 } }, {
  });

  const filteredEmailTemplateNames = useMemo(() => {
    if (!templates || !emailTemplateNames) { return []; }

    const templateNames = selectedCategory.length > 0
      ? templates.filter((template) => {
        return selectedCategory.every((selected) => {
          return template.email_template_categories.some((cat) => {
            return selected.id === cat.id;
          });
        });
      })
      : templates;

    return emailTemplateNames
      .filter((template) => {
        return templateNames.some(
          (desc) => { return desc.name === template.Name; },
        );
      })
      .filter((tN) => { return !ignoredTemplated.includes(tN.Name); })
      .sort((a, b) => { return (a.Name > b.Name ? 1 : -1); });
  }, [ selectedCategory, emailTemplateNames, ignoredTemplated, templates ]);

  if (emailTemplateNamesLoading || templateCategoriesLoading) {
    return (
      <LoaderSkeleton />
    );
  }

  return (
    <div>
      <div style={ { display: 'flex', justifyContent: 'center', gap: '10px' } }>
        <Autocomplete
          multiple
          options={ templateCategories }
          getOptionLabel={ (option) => { return option.name; } }
          color='primary'
          style={ { width: 'auto', minWidth: '300px', maxWidth: '600px' } }
          value={ selectedCategory }
          onChange={ (event, newValue) => {
            setSelectedCategory(newValue);
          } }
          renderInput={ (params) => {
            return (
              <TextField { ...params } label='Filter by Category' variant='outlined' />
            );
          } }
        />

        <Autocomplete
          options={ filteredEmailTemplateNames }
          getOptionLabel={ (option) => { return option.Name; } }
          color='primary'
          style={ { width: '500px' } }
          onChange={ (event, newValue) => {
            if (newValue) {
              setSelectedTemplate(newValue.Name);
            }
          } }
          renderOption={ (props, option) => {
            return (
              <span
                { ...props }
                style={ { backgroundColor: (option.selected || props.Name === selectedTemplate) ? 'lightblue' : '', width: '100%' } }
              >
                {props.Name}
              </span>
            );
          } }
          renderInput={ (params) => {
            return (
              <TextField
                { ...params }
                label='Email Templates'
              />
            );
          } }
        />

      </div>

      <div style={ { marginTop: '20px' } }>
        {
          '' !== selectedTemplate ? (
            <EmailTemplate
              selectedTemplate={ selectedTemplate }
              setSelectedTemplate={ setSelectedTemplate }
            />
          ) : (
            <div style={ { height: '77vh', display: 'flex', alignItems: 'center', justifyContent: 'center' } }>
              <Typography variant='h6'>
                Select a template
              </Typography>
            </div>
          )
        }
      </div>
    </div>
  );
};

export default EmailTemplates;
